import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import {
  Plus, Briefcase, Lightning, Folder as FolderIcon, FileText, Receipt, User, Buildings, CaretDown, Moon,
} from "@phosphor-icons/react";
import { isGNavArmed } from "../lib/keyboardState";
import { useAuth } from "../contexts/AuthContext";
import { hasOohAccess } from "./navSections";

/**
 * Quick-create dropdown shown in the topbar. Each item routes the user to the
 * relevant list page with `?new=1` so the page's existing create dialog opens
 * automatically — no duplicated form code.
 *
 * Keyboard shortcut: press `c` (when no input/textarea/contentEditable is focused
 * and no other modal is open) to pop the menu open. Same pattern as GitHub.
 */
export default function QuickCreateMenu() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  // Global "c" hotkey
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== "c" && e.key !== "C") return;
      // Skip if the user is typing somewhere
      const t = e.target;
      if (!t) return;
      const tag = (t.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || t.isContentEditable) return;
      // Skip if a Radix dialog is open (don't hijack key while filling a form)
      if (document.querySelector('[role="dialog"][data-state="open"]')) return;
      // Skip modifier-key combos (Ctrl/Cmd/Alt + C is copy etc.)
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      // Skip when `g` was just pressed — `g c` is a nav shortcut to Clients.
      if (isGNavArmed()) return;
      e.preventDefault();
      setOpen(prev => !prev);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const items = [
    { label: "Job",            icon: Briefcase, href: "/jobs?new=1",           testid: "quick-create-job" },
    { label: "Emergency Job",  icon: Lightning, href: "/emergency-jobs?new=1", testid: "quick-create-emergency", danger: true },
    ...(hasOohAccess(user) ? [{ label: "OOH Request", icon: Moon, href: "/ooh?new=1", testid: "quick-create-ooh", indigo: true }] : []),
    { label: "Project",        icon: FolderIcon, href: "/projects?new=1",      testid: "quick-create-project" },
    { sep: true },
    { label: "Quote",          icon: FileText,  href: "/quotes/new",           testid: "quick-create-quote" },
    { label: "Invoice",        icon: Receipt,   href: "/invoices/new",         testid: "quick-create-invoice" },
    { sep: true },
    { label: "Client",         icon: User,      href: "/clients?new=1",        testid: "quick-create-client" },
    { label: "Supplier",       icon: Buildings, href: "/vendors?new=1",        testid: "quick-create-supplier" },
  ];

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          className="h-8 gap-1.5 bg-primary hover:bg-primary/90 text-primary-foreground"
          title="Quick create — press C"
          data-testid="quick-create-trigger"
        >
          <Plus size={14} weight="bold" />
          <span className="hidden sm:inline">Create</span>
          <kbd className="hidden md:inline-flex h-4 min-w-[16px] items-center justify-center rounded border border-primary-foreground/30 bg-primary-foreground/10 px-1 text-[9px] font-semibold uppercase tracking-wider text-primary-foreground/80" data-testid="quick-create-hotkey-hint">
            C
          </kbd>
          <CaretDown size={10} className="opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52" data-testid="quick-create-menu">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">Quick Create</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.map((it, i) =>
          it.sep ? (
            <DropdownMenuSeparator key={`sep-${i}`} />
          ) : (
            <DropdownMenuItem
              key={it.testid}
              onClick={() => navigate(it.href)}
              className={`gap-2 ${it.danger ? "text-red-600 focus:text-red-700 dark:text-red-400" : it.indigo ? "text-indigo-700 focus:text-indigo-800 dark:text-indigo-300" : ""}`}
              data-testid={it.testid}
            >
              <it.icon size={14} className="shrink-0" />
              <span>{it.label}</span>
            </DropdownMenuItem>
          )
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
